import classNames from "classnames";
import { motion } from "framer-motion";
import MixConsole from "../molecules/MixConsole/MixConsole";
import scrollElementIntoViewIfNeeded from "util/scrollElementIntoViewIfNeeded";
import useSelectedMixPanelPosition from "../hooks/useSelectedMixPanelPosition";
import { State } from "../state/reducer";
import { Dispatch } from "react";
import Action from "../state/actions";
import {
  getActiveInstrument,
  getInstrument,
  getInstrumentsAvailable,
} from "../state/selectors";
import MixConsoleReportCard from "../molecules/MixConsole/MixConsoleReportCard";
import ChannelReportCardStrip from "../molecules/Channels/ChannelReportCardStrip";
import ChannelStrip from "../molecules/Channels/ChannelStrip";

interface Props {
  state: State;
  dispatch: Dispatch<Action>;
  reportCardMode: boolean;
}

export default function DesktopConsoleAndChannelStrips({
  state,
  dispatch,
  reportCardMode,
}: Props) {
  const instruments = getInstrumentsAvailable(state);
  const instrument = getActiveInstrument(state);
  const { consoleStyle, containerRef, consoleRef, channelStripRefs } =
    useSelectedMixPanelPosition({ instrument, instruments });

  return (
    <div className={classNames("grid grid-cols-2 rounded rounded-tr-none", {})}>
      {/* channel strips container */}
      <div
        ref={containerRef}
        className={classNames("border-t-4 border-slate-300 pt-2")}
      >
        {instruments.map((channelInstrument, i) => {
          const first = i === 0;
          const last = i === instruments.length - 1;

          return (
            <div
              key={channelInstrument}
              className={classNames({ "mb-4": !last })}
              ref={(element) => {
                channelStripRefs.current[i] = element;
              }}
            >
              {reportCardMode ? (
                <ChannelReportCardStrip
                  active={channelInstrument === instrument.instrument}
                  instrument={getInstrument(state, channelInstrument)}
                  state={state}
                  dispatch={dispatch}
                />
              ) : (
                <ChannelStrip
                  active={channelInstrument === instrument.instrument}
                  instrument={getInstrument(state, channelInstrument)}
                  state={state}
                  dispatch={dispatch}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* mixconsole container */}
      <div className="relative border-4 border-b-0 border-l-0 border-slate-300 px-2">
        <div className={"relative h-full"}>
          <motion.div
            initial={false}
            layout
            transition={{ duration: 0.1 }}
            className="absolute inset-x-0"
            style={consoleStyle}
            ref={consoleRef}
            onLayoutAnimationComplete={() => {
              // FIXME: idk if this is the best ux but the user needs to see the points and lives header
              // so scorlling to the top after submit makes sense and this woudl cause it to scroll back down which doesn't make sense
              // don't scroll for report card mode so user can see the points change
              if (reportCardMode) {
                return;
              }

              scrollElementIntoViewIfNeeded(consoleRef.current);
            }}
          >
            {reportCardMode ? (
              <MixConsoleReportCard
                state={state}
                instrument={instrument}
                dispatch={dispatch}
              />
            ) : (
              <MixConsole
                state={state}
                instrument={instrument}
                onRequestPlaybackChange={(instrument, params) =>
                  dispatch({
                    type: "requestPlaybackChange",
                    payload: {
                      instrument,
                      params,
                    },
                  })
                }
              />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
